/**
 * Administração › Perfis de acesso (ADR 0013, adendo 1).
 *
 * O que um perfil carrega, visível: matriz módulo × (liberado, nível), quem
 * usa, histórico de versões com diff, e a lista das regras ainda cravadas no
 * código (backlog da issue #78). Perfil de fábrica é exemplo: copia-se, não
 * se edita. A operadora é dona dos seus.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Copy, Edit, X, Save, Lock, History, Users, AlertCircle, ShieldAlert, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { perfisAcessoAPI } from '../services/api';

const NOMES_SECOES = {
  operacional: 'Operacional', analises: 'Análises', comunicacoes: 'Comunicações',
  administracao: 'Administração', servicos: 'Serviços', universidade: 'Universidade',
};
const ROT_NIVEL = { leitor: 'Leitor', operador: 'Operador', gestor: 'Gestor' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const AdminPerfisAcesso = () => {
  const [perfis, setPerfis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aberto, setAberto] = useState(null);          // perfil detalhado (obter)
  const [modo, setModo] = useState('ver');             // ver | editar | novo
  const [form, setForm] = useState({ nome: '', descricao: '', modulos: {} });
  const [salvando, setSalvando] = useState(false);
  const [regras, setRegras] = useState(null);
  const [mostrarRegras, setMostrarRegras] = useState(false);
  const [copiaDe, setCopiaDe] = useState(null);        // perfil de origem ao copiar
  const [nomeCopia, setNomeCopia] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await perfisAcessoAPI.listar();
      setPerfis(data.perfis || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar perfis de acesso');
    }
    setLoading(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrir = async (id, novoModo = 'ver') => {
    try {
      const { data } = await perfisAcessoAPI.obter(id);
      setAberto(data);
      setForm({
        nome: data.nome, descricao: data.descricao || '',
        modulos: Object.fromEntries((data.modulos || []).map(m => [m.modulo, { liberado: m.liberado, nivel_padrao: m.nivel_padrao || '' }])),
      });
      setModo(novoModo);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao abrir o perfil');
    }
  };

  const novo = async () => {
    // matriz vazia sobre o catálogo do primeiro perfil (só para ter a lista de módulos)
    const base = perfis[0];
    if (!base) return;
    const { data } = await perfisAcessoAPI.obter(base.id);
    setAberto({ ...data, id: null, nome: '', descricao: '', de_fabrica: false, versao: 1, historico: [], usuarios: [] });
    setForm({ nome: '', descricao: '', modulos: Object.fromEntries((data.modulos || []).map(m => [m.modulo, { liberado: false, nivel_padrao: '' }])) });
    setModo('novo');
  };

  const salvar = async () => {
    if (!form.nome.trim()) { toast.error('Dê um nome ao perfil'); return; }
    setSalvando(true);
    const modulos = Object.entries(form.modulos)
      .filter(([, v]) => v.liberado || v.nivel_padrao)
      .map(([modulo, v]) => ({ modulo, liberado: !!v.liberado, nivel_padrao: v.nivel_padrao || null }));
    try {
      if (modo === 'novo') {
        const { data } = await perfisAcessoAPI.criar({ nome: form.nome, descricao: form.descricao, modulos });
        toast.success('Perfil criado');
        await carregar();
        await abrir(data.id, 'ver');
      } else {
        const { data } = await perfisAcessoAPI.editar(aberto.id, { nome: form.nome, descricao: form.descricao, modulos });
        toast.success(data.mudou ? `Versão ${data.versao} gravada (${data.usuarios_afetados} usuário(s) afetado(s))` : 'Nada mudou');
        await carregar();
        await abrir(aberto.id, 'ver');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
    setSalvando(false);
  };

  const copiar = async () => {
    if (!nomeCopia.trim()) { toast.error('Dê um nome à cópia'); return; }
    try {
      const { data } = await perfisAcessoAPI.copiar(copiaDe.id, { nome: nomeCopia });
      toast.success(`Cópia criada: ${data.nome}`);
      setCopiaDe(null); setNomeCopia('');
      await carregar();
      await abrir(data.id, 'editar');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao copiar');
    }
  };

  const verRegras = async () => {
    if (regras === null) {
      try { const { data } = await perfisAcessoAPI.regrasFixas(); setRegras(data); } catch (e) { setRegras({ total: 0, regras: [] }); }
    }
    setMostrarRegras(v => !v);
  };

  const editavel = modo !== 'ver';
  const porSecao = (mods) => {
    const g = {};
    (mods || []).forEach(m => { (g[m.secao] = g[m.secao] || []).push(m); });
    return Object.keys(NOMES_SECOES).filter(s => g[s]).map(s => [s, g[s]]);
  };

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div className="search-box" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ color: 'var(--gray-800)', margin: 0 }}>Perfis de acesso</h2>
            <small style={{ color: 'var(--gray-500)' }}>O que cada perfil libera, em que nível, quem usa e o histórico. Perfil de fábrica é exemplo: copie e edite a cópia.</small>
          </div>
          <button className="btn btn-primary" onClick={novo}><Plus size={18} /> Novo perfil</button>
        </div>
      </div>

      {loading ? <p style={{ color: 'var(--gray-500)' }}>Carregando…</p> : perfis.length === 0 ? (
        <div className="empty-state"><AlertCircle size={48} /><p>Nenhum perfil de acesso</p></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px', marginBottom: '1.5rem' }}>
          {perfis.map(p => (
            <div key={p.id} onClick={() => abrir(p.id)} style={{ background: '#fff', border: `1px solid ${aberto?.id === p.id ? 'var(--brand, #20a490)' : 'var(--gray-200)'}`, borderRadius: '10px', padding: '12px 14px', cursor: 'pointer', opacity: p.ativo ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <strong style={{ color: 'var(--gray-800)' }}>{p.nome}</strong>
                {p.de_fabrica ? <span title="Perfil de fábrica: não se edita, copia-se" style={{ fontSize: '0.7rem', background: 'var(--gray-100)', color: 'var(--gray-600)', padding: '2px 6px', borderRadius: '6px', display: 'inline-flex', gap: '4px', alignItems: 'center' }}><Lock size={11} /> fábrica</span>
                  : <span style={{ fontSize: '0.7rem', background: '#e8f6f4', color: '#1a8a79', padding: '2px 6px', borderRadius: '6px' }}>v{p.versao}</span>}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: '4px' }}>{p.descricao || '—'}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--gray-600)', marginTop: '8px', display: 'flex', gap: '12px' }}>
                <span>{p.modulos_liberados} módulo{p.modulos_liberados === 1 ? '' : 's'}</span>
                <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}><Users size={12} /> {p.usuarios}</span>
                {!p.ativo && <span style={{ color: '#e74c3c' }}>inativo</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {aberto && (
        <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '260px' }}>
              {editavel ? (
                <>
                  <input className="form-control" placeholder="Nome do perfil" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} style={{ fontWeight: 700, marginBottom: '6px' }} />
                  <input className="form-control" placeholder="Descrição (para quem é este perfil)" value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} />
                </>
              ) : (
                <>
                  <h3 style={{ margin: 0, color: 'var(--gray-800)' }}>{aberto.nome} {aberto.de_fabrica && <Lock size={14} style={{ verticalAlign: 'middle', color: 'var(--gray-500)' }} />}</h3>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>{aberto.descricao || '—'} · versão {aberto.versao} · atualizado em {fmt(aberto.atualizado_em)}</div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {!editavel && !aberto.de_fabrica && <button className="btn btn-secondary" onClick={() => setModo('editar')}><Edit size={15} /> Editar</button>}
              {!editavel && aberto.id && <button className="btn btn-secondary" onClick={() => { setCopiaDe(aberto); setNomeCopia(`${aberto.nome} (cópia)`); }}><Copy size={15} /> Copiar</button>}
              {editavel && <button className="btn btn-primary" disabled={salvando} onClick={salvar}><Save size={15} /> {modo === 'novo' ? 'Criar' : 'Gravar versão'}</button>}
              <button className="btn btn-secondary" onClick={() => { setAberto(null); setModo('ver'); }}><X size={15} /></button>
            </div>
          </div>
          {aberto.de_fabrica && modo === 'ver' && (
            <div style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--gray-600)', background: 'var(--gray-100)', padding: '6px 10px', borderRadius: '6px' }}>
              Perfil de fábrica: o produto o mantém como exemplo. Para adaptar, copie e edite a cópia; assim uma atualização do AURA nunca muda o acesso de ninguém.
            </div>
          )}

          {/* Matriz */}
          <div style={{ marginTop: '1rem' }}>
            {porSecao(aberto.modulos).map(([secao, mods]) => (
              <div key={secao} style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '4px' }}>{NOMES_SECOES[secao]}</div>
                {mods.map(m => {
                  const v = form.modulos[m.modulo] || { liberado: m.liberado, nivel_padrao: m.nivel_padrao || '' };
                  return (
                    <div key={m.modulo} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 8px', borderBottom: '1px solid var(--gray-100)', opacity: v.liberado ? 1 : 0.65 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, cursor: editavel ? 'pointer' : 'default', fontSize: '0.9rem' }}>
                        <input type="checkbox" checked={!!v.liberado} disabled={!editavel} onChange={e => setForm({ ...form, modulos: { ...form.modulos, [m.modulo]: { ...v, liberado: e.target.checked } } })} />
                        {m.nome}
                      </label>
                      {(m.niveis || []).length > 1 ? (
                        <select value={v.nivel_padrao || m.nivel_base || ''} disabled={!editavel || !v.liberado}
                          onChange={e => setForm({ ...form, modulos: { ...form.modulos, [m.modulo]: { ...v, nivel_padrao: e.target.value === m.nivel_base ? '' : e.target.value } } })}
                          style={{ fontSize: '0.8rem', padding: '2px 6px', borderRadius: '6px', border: '1px solid var(--gray-300)' }}>
                          {m.niveis.map(n => <option key={n} value={n}>{ROT_NIVEL[n]}{n === m.nivel_base ? ' (base)' : ''}</option>)}
                        </select>
                      ) : <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{v.liberado ? 'acesso completo' : ''}</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {modo === 'ver' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginTop: '1rem' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', display: 'flex', gap: '6px', alignItems: 'center' }}><Users size={15} /> Quem usa ({aberto.usuarios?.length || 0})</div>
                {(aberto.usuarios || []).length === 0 ? <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>Ninguém ainda.</div> :
                  <ul style={{ margin: '6px 0 0', paddingLeft: '18px', fontSize: '0.85rem' }}>{aberto.usuarios.map(u => <li key={u.id}>{u.nome} <span style={{ color: 'var(--gray-500)' }}>· {u.email}</span></li>)}</ul>}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', display: 'flex', gap: '6px', alignItems: 'center' }}><History size={15} /> Histórico</div>
                {(aberto.historico || []).map(h => (
                  <div key={h.versao} style={{ fontSize: '0.82rem', padding: '6px 0', borderBottom: '1px solid var(--gray-100)' }}>
                    <div><strong>v{h.versao}</strong> · {fmt(h.quando)} · {h.resumo}{h.usuarios_afetados ? ` · ${h.usuarios_afetados} usuário(s) afetado(s)` : ''}</div>
                    {h.diff?.modulos && (
                      <ul style={{ margin: '4px 0 0', paddingLeft: '18px', color: 'var(--gray-600)' }}>
                        {Object.entries(h.diff.modulos).map(([mod, d]) => (
                          <li key={mod}>{mod}: {d.de.liberado ? 'liberado' : 'sem acesso'}{d.de.nivel_padrao ? ` (${ROT_NIVEL[d.de.nivel_padrao]})` : ''} → {d.para.liberado ? 'liberado' : 'sem acesso'}{d.para.nivel_padrao ? ` (${ROT_NIVEL[d.para.nivel_padrao]})` : ''}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Regras fixas ainda no código */}
      <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '1rem 1.25rem' }}>
        <button type="button" onClick={verRegras} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray-800)', fontWeight: 600 }}>
          {mostrarRegras ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <ShieldAlert size={16} color="#f39c12" /> Regras fixas do produto{regras ? ` (${regras.total})` : ''}
        </button>
        <small style={{ display: 'block', color: 'var(--gray-500)', marginTop: '4px' }}>
          Verificações de perfil ainda escritas no código, fora destes perfis. Enquanto existirem, valem por cima do que está aqui. Cada uma some desta lista quando migra para os níveis por módulo (issue #78).
        </small>
        {mostrarRegras && regras && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', marginTop: '10px' }}>
            <thead><tr style={{ color: 'var(--gray-600)', textAlign: 'left' }}><th style={{ padding: '4px 6px' }}>Rota</th><th style={{ padding: '4px 6px' }}>Perfis</th><th style={{ padding: '4px 6px' }}>Onde</th></tr></thead>
            <tbody>
              {regras.regras.map((r, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{r.rota || '—'}</td>
                  <td style={{ padding: '4px 6px' }}>{(r.perfis || []).join(', ') || '—'}</td>
                  <td style={{ padding: '4px 6px', color: 'var(--gray-500)', fontFamily: 'monospace' }}>{r.arquivo}:{r.linha}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {copiaDe && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '460px' }}>
            <div className="modal-header"><h3>Copiar "{copiaDe.nome}"</h3><button className="modal-close" onClick={() => setCopiaDe(null)}><X size={18} /></button></div>
            <div className="form-group"><label>Nome da cópia</label><input className="form-control" value={nomeCopia} onChange={e => setNomeCopia(e.target.value)} autoFocus /></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCopiaDe(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={copiar}><Copy size={15} /> Copiar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPerfisAcesso;
